import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

type LanguageCode = 'ko' | 'en' | 'es';

interface BlockInfo {
  blockCode: string;
  subFolder: string;
  language: LanguageCode;
  showEmailForm: boolean;
}

// 블록 코드와 subFolder 매핑
const BLOCK_CODE_TO_SUBFOLDER: Record<string, string> = {
  BLK_005: 'fellin',
  BLK_018: 'ces',
  BLK_020: 'sfpoc',
};

// 블록별 언어 설정, 기본값 'ko'
const BLOCK_LANGUAGE_CONFIG: Record<string, LanguageCode> = {
  BLK_005: 'en',
  BLK_018: 'en',
  BLK_020: 'en',
};

// 이메일 폼을 표시할 블록 코드 목록
const EMAIL_FORM_BLOCKS = ['BLK_005', 'BLK_018', 'BLK_019', 'BLK_020'];

/**
 * QR 코드 페이지에서 logoUrl을 기반으로 블록 정보를 추출하는 커스텀 훅
 * logoUrl 예시: "https://pb-server.projectbuildup.io/component/logo/BLK_005_ko.png"
 *
 * @param logoUrl - QR 코드 응답에서 받은 logoUrl
 * @returns 블록 정보 (블록 코드, subFolder, 언어, 이메일 폼 표시 여부)
 *
 * @example
 * const { blockCode, subFolder, showEmailForm } = useQrBlockInfo(qrData.logoUrl);
 */
export const useQrBlockInfo = (logoUrl: string) => {
  const { i18n } = useTranslation();

  const blockInfo = useMemo((): BlockInfo | null => {
    if (!logoUrl) return null;

    const match = logoUrl.match(/BLK_\d+/);
    if (!match) return null;

    const blockCode = match[0];
    const subFolder = BLOCK_CODE_TO_SUBFOLDER[blockCode] || '';
    const language = BLOCK_LANGUAGE_CONFIG[blockCode] || 'ko';
    // Dev 환경에서는 모든 블록에서 이메일 폼을 비활성화
    const showEmailForm = import.meta.env.DEV ? false : EMAIL_FORM_BLOCKS.includes(blockCode);

    return {
      blockCode,
      subFolder,
      language,
      showEmailForm,
    };
  }, [logoUrl]);

  // 블록에 맞는 언어로 자동 전환
  useEffect(() => {
    if (blockInfo && blockInfo.language && i18n.language !== blockInfo.language) {
      i18n.changeLanguage(blockInfo.language);
    }
  }, [blockInfo, i18n]);

  return blockInfo;
};
